import { type FunctionComponent, useState, useEffect } from 'react';

import classnames from 'classnames';

import type { UtvidetInformasjonVeiledningstjeneste, Apningstid } from '../../../types/verktoynetcoreentities';
import type { JSX } from 'react/jsx-runtime';

import Loader from '@helsenorge/designsystem-react/components/Loader';

import { getDayText, getOpenClosedTexts } from './utils';
import { getChatCardData } from '../../../utils/api-utils';
import ChatBubble from '../../../utils/Icons/ChatCard/ChatBubble';
import Phone from '../../../utils/Icons/ChatCard/Phone';

import styles from './styles.module.scss';

type ChatCardsTexts = {
  openClosed?: { status: string; time: string };
  title: { text: string; href: string };
  topics: string[];
  tabs: { buttons: string[]; content: React.ReactNode[] };
  bubbleContact: { href: string; ariaLabel: string };
  phoneContact?: { href: string; ariaLabel: string };
};

interface ChatCardProps {
  texts: ChatCardsTexts;
}

const ChatCard: FunctionComponent<ChatCardProps> = (props: ChatCardProps) => {
  const { texts } = props;

  const [selectedTab, setSelectedTab] = useState(0);

  const openClosedTextClasses = classnames(
    styles['chat-card__header__open-closed-text'],
    styles['chat-card__header__open-closed-text--bold']
  );

  const renderTabButton = (text: string, index: number): React.JSX.Element => {
    const tabButtonClasses = classnames(styles['chat-card__tab-button'], {
      [styles['chat-card__tab-button--active']]: selectedTab === index,
    });

    return (
      <button key={index} onClick={() => setSelectedTab(index)} className={tabButtonClasses}>
        {text}
      </button>
    );
  };

  const renderTabContent = (children: React.ReactNode, index: number): React.JSX.Element => {
    const tabContentClasses = classnames(styles['chat-card__tab-content'], {
      [styles['chat-card__tab-content--active']]: selectedTab === index,
    });

    return (
      <div key={index} className={tabContentClasses}>
        {children}
      </div>
    );
  };

  return (
    <div className={styles['chat-card']}>
      <div>
        {texts.openClosed && (
          <p className={styles['chat-card__header__open-closed-text']}>
            <span className={openClosedTextClasses}>{texts.openClosed.status}</span>
            {' - '}
            {texts.openClosed.time}
          </p>
        )}
        {texts.title && (
          <a href={texts.title.href} target={'_blank'} className={styles['chat-card__header__anchor-title']} rel="noreferrer">
            {texts.title.text}
          </a>
        )}
        {texts.topics && (
          <div className={styles['chat-card__header__topic-wrapper']}>
            {texts.topics.map((topic, index) => {
              return (
                <span key={index} className={styles['chat-card__header__topic']}>
                  {topic}
                </span>
              );
            })}
          </div>
        )}
      </div>
      {texts.tabs && (
        <div className={styles['chat-card__content']}>
          <div role={'region'}>
            <div className={styles['chat-card__tab-list']} role={'tablist'}>
              {texts.tabs.buttons.map((button, index) => {
                return renderTabButton(button, index);
              })}
            </div>
            {texts.tabs.content.map((c, index) => {
              return renderTabContent(c, index);
            })}
          </div>
        </div>
      )}
      <div className={styles['chat-card__contact-wrapper']}>
        {texts.bubbleContact && (
          <a
            href={texts.bubbleContact.href}
            target="_blank"
            className={styles['chat-card__contact-button']}
            aria-label={texts.bubbleContact.ariaLabel}
            rel="noreferrer"
          >
            {<ChatBubble />}
          </a>
        )}
        {texts.phoneContact && (
          <a
            href={texts.phoneContact.href}
            target="_blank"
            className={styles['chat-card__contact-button']}
            aria-label={texts.phoneContact.ariaLabel}
            rel="noreferrer"
          >
            {<Phone />}
          </a>
        )}
      </div>
    </div>
  );
};

interface ChatCardWrapperProps {
  toolId: string;
}

const ChatCardWrapper: FunctionComponent<ChatCardWrapperProps> = (props: ChatCardWrapperProps) => {
  const [chatCardData, setChatCardData] = useState<UtvidetInformasjonVeiledningstjeneste>();
  const [error, setError] = useState<string>();

  const getChatCardText = (): ChatCardsTexts | undefined => {
    if (!chatCardData || typeof chatCardData.Apningstid === 'undefined') return;

    const openTime = chatCardData.Apningstid;
    const hasOpeningHours = openTime && openTime.length > 0 && openTime.some(time => time.FraKl || time.TilKl);
    const hasPhoneNumber = chatCardData.Telefon && chatCardData.Telefon.trim() !== '';
    const hasStaffing = chatCardData.BemanningTypeNavn && chatCardData.BemanningTypeNavn.trim() !== '';

    const openClosedTexts = hasOpeningHours ? getOpenClosedTexts(openTime) : null;

    const tabButtons = ['Info'];
    const tabContent = [
      <p key={0} className={styles['chat-card__content-text']}>
        {chatCardData.Ingress ?? chatCardData.Beskrivelse}
      </p>,
    ];

    if (hasOpeningHours) {
      tabButtons.push('Åpningstider');
      tabContent.push(
        <dl key={tabContent.length} className={styles['chat-card__dl']}>
          {renderOpenDays(chatCardData)}
        </dl>
      );
    }

    if (hasStaffing) {
      tabButtons.push('Bemanning');
      tabContent.push(
        <p key={tabContent.length} className={styles['chat-card__content-text']}>
          {`Det er ${chatCardData.BemanningTypeNavn} som bemanner denne.`}
        </p>
      );
    }

    const newChatCardsText: ChatCardsTexts = {
      openClosed: openClosedTexts ? { status: openClosedTexts.statusText, time: openClosedTexts.timeText } : undefined,
      title: {
        text: chatCardData.Navn,
        href: chatCardData.Lenker[0],
      },
      topics: chatCardData.Sokeord.map(so => so.toUpperCase()),
      tabs: {
        buttons: tabButtons,
        content: tabContent,
      },
      bubbleContact: {
        href: chatCardData.Lenker[0],
        ariaLabel: `Åpne chat for ${chatCardData.Navn}`,
      },
      phoneContact: hasPhoneNumber
        ? {
            href: `tel:${chatCardData.Telefon}`,
            ariaLabel: `Ring ${chatCardData.Navn} på nummer ${chatCardData.Telefon}`,
          }
        : undefined,
    };

    return newChatCardsText;
  };

  const fetchCardData = async (): Promise<void> => {
    try {
      const chatCardJson = await getChatCardData(props.toolId);
      const chatCardJsonString = JSON.stringify(chatCardJson);
      const newChatCardData: UtvidetInformasjonVeiledningstjeneste = JSON.parse(chatCardJsonString)[
        'Veiledningstjeneste'
      ] as UtvidetInformasjonVeiledningstjeneste;

      newChatCardData.Apningstid = newChatCardData.Apningstid.map((at: Apningstid) => {
        at.FraKl = at.FraKl ? at.FraKl.slice(0, -3) : '';
        at.TilKl = at.TilKl ? at.TilKl.slice(0, -3) : '';
        return at;
      });

      setChatCardData(newChatCardData);
    } catch {
      setError('Kunne ikke hente chattetjenester, prøv igjen.');
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchCardData();
  }, []);

  const renderOpenDays = (cardData: UtvidetInformasjonVeiledningstjeneste): JSX.Element[] => {
    const dddtArray = [];

    for (let i = 1; i < 7; i++) {
      const ot = cardData.Apningstid.find(at => at.Dag === i);
      const dt = <dt key={'dt' + i}>{getDayText(i)}</dt>;
      const dd = <dd key={'dd' + i}>{ot?.FraKl ? ot.FraKl + ' - ' + ot.TilKl : 'Stengt'}</dd>;
      dddtArray.push(dt, dd);
    }

    return dddtArray;
  };

  const chatCardText = getChatCardText();

  if (!chatCardText && !error) {
    return <Loader />;
  }

  if (error) {
    return <p className={styles.error}>{error}</p>;
  }

  if (!chatCardText) {
    return null;
  }

  return <ChatCard texts={chatCardText} />;
};

export default ChatCardWrapper;
